import { formatNumber } from "@borealis/number-format";
import { Trans, useLingui } from "@lingui/react";
import type { AgricultureAd, ConstructionAd } from "@mobility-item-page/types";
import { assertNever, formatPrice, getId } from "@mobility-item-page/utilities";
import { useAdViewAd } from "src/context/AdViewProviderContext.tsx";
import { useAppConfig } from "../../context/AppConfigProviderContext.tsx";

const Price = () => {
  const ad = useAdViewAd();
  const { i18n } = useLingui();
  const { brand, locale } = useAppConfig();

  if (ad.price === undefined) {
    return null;
  }

  const priceLabel = i18n.t({
    id: "itemPageLayout.adPrice.price",
    message: "Price",
    comment: "Price",
  });
  const totalPriceLabel = i18n.t({
    id: "itemPageLayout.adPrice.totalPrice",
    message: "Total price",
    comment: "Total price",
  });
  const rentPriceLabel = i18n._({
    id: "itemPageLayout.adPrice.rentPrice",
    message: "Rent price",
    comment: "Rent price",
  });
  const excludingVAT = (price?: number): string => {
    return i18n.t({
      id: "itemPageLayout.adPrice.excludingVAT",
      message: "({amount}excl. VAT)",
      values: {
        amount: price ? `${formatPrice(price, locale, brand)} ` : "",
      },
      comment: "Price suffix for price excluding VAT, and subtitle for ads containing preVat price",
    });
  };
  const includingVATSuffix = i18n.t({
    id: "itemPageLayout.adPrice.suffix.includingVAT",
    message: "(incl. VAT)",
    comment: "Price suffix for price including VAT",
  });

  const adviewType = ad.adViewType;

  const getVATPriceSuffix = (ad: AgricultureAd | ConstructionAd) => {
    if (ad.price?.vat_included === undefined) {
      return undefined;
    }
    return ad.price.vat_included ? includingVATSuffix : excludingVAT();
  };

  switch (adviewType) {
    case "car-new":
    case "car-used":
    case "car-used-business": {
      if ("preVat" in ad.price && ad.price.preVat) {
        return <PriceSingular price={ad.price.total} title={priceLabel} subtitle={excludingVAT(ad.price.preVat)} />;
      }
      // registration class 8 applies only to DBA ads
      if ("registration_class" in ad && getId(ad.registration_class) === 8) {
        return <PriceSingular price={ad.price.total} title={priceLabel} priceSuffix={excludingVAT()} />;
      }
      return <PriceSingular price={ad.price.total} title={totalPriceLabel} />;
    }
    case "car-leasing":
    case "car-leasing-business": {
      const leasingPriceLabel = i18n._({
        id: "itemPageLayout.adPrice.leasingPrice",
        message: "Leasing price",
        comment: "Monthly Leasing price",
      });
      const leasingDepositPriceLabel = i18n._({
        id: "itemPageLayout.adPrice.leasingDepositPrice",
        message: "Deposit",
        comment: "Leasing Deposit price",
      });
      return (
        <PriceDouble
          priceOne={ad.price.monthly ?? 0}
          priceTwo={ad.price.initial ?? 0}
          textOne={leasingPriceLabel}
          textTwo={leasingDepositPriceLabel}
          priceOneSubtitle={ad.price.monthlyPreVat ? excludingVAT(ad.price.monthlyPreVat) : undefined}
        />
      );
    }
    case "boat-sale":
    case "boat-new-sale":
    case "boat-wanted":
    case "boat-motor":
    case "boat-dock": {
      return <PriceSingular price={ad.price} title={priceLabel} />;
    }
    case "boat-rent": {
      return <PriceRange priceFrom={ad.price.from} priceTo={ad.price.to} text={rentPriceLabel} />;
    }
    case "motorcycle":
    case "atv":
    case "moped":
    case "snowmobile": {
      const totalPrice =
        ad.price.net && ad.price.registration_price ? ad.price.net + ad.price.registration_price : ad.price.net;

      if (ad.price.preVat) {
        return <PriceSingular price={totalPrice} title={priceLabel} subtitle={excludingVAT(ad.price.preVat)} />;
      }
      return <PriceSingular price={totalPrice} title={priceLabel} />;
    }
    case "mobile-home":
    case "caravan": {
      if (ad.price.monthly) {
        return <PriceSingular title={rentPriceLabel} price={ad.price.monthly} />;
      }
      const totalPrice =
        ad.price.net && ad.price.registration_price ? ad.price.net + ad.price.registration_price : ad.price.net;

      if (ad.price.preVat) {
        return <PriceSingular price={totalPrice} title={priceLabel} subtitle={excludingVAT(ad.price.preVat)} />;
      }
      return <PriceSingular price={totalPrice} title={priceLabel} />;
    }
    case "agriculture-tractor":
    case "agriculture-thresher":
    case "agriculture-tools": {
      if (ad.price.preVat) {
        return <PriceSingular price={ad.price.net} title={priceLabel} subtitle={excludingVAT(ad.price.preVat)} />;
      }
      return <PriceSingular price={ad.price.net} title={priceLabel} priceSuffix={getVATPriceSuffix(ad)} />;
    }

    case "truck":
    case "a-tractor": {
      if (ad.price.preVat) {
        return <PriceSingular price={ad.price.net} title={priceLabel} subtitle={excludingVAT(ad.price.preVat)} />;
      }
      return <PriceSingular price={ad.price.net} title={priceLabel} />;
    }

    case "construction": {
      if (ad.price.preVat) {
        return <PriceSingular price={ad.price.total} title={priceLabel} subtitle={excludingVAT(ad.price.preVat)} />;
      }
      return <PriceSingular price={ad.price.total} title={priceLabel} priceSuffix={getVATPriceSuffix(ad)} />;
    }
    case "bus": {
      if (ad.price.preVat) {
        return <PriceSingular price={ad.price.total} title={priceLabel} subtitle={excludingVAT(ad.price.preVat)} />;
      }
      return <PriceSingular price={ad.price.total} title={priceLabel} />;
    }

    default:
      assertNever(adviewType);
      return null;
  }
};

export default Price;

const PriceSingular = ({
  price,
  title,
  subtitle,
  priceSuffix,
}: {
  price?: number;
  title: string;
  subtitle?: string;
  priceSuffix?: string;
}) => {
  const { brand, locale } = useAppConfig();
  if (price === undefined) return null;
  return (
    <div className="border-t pt-40 mt-40">
      <div className="flex flex-col">
        <p className="s-text-subtle mb-0">{title}</p>
        <h2 className="mb-0 mt-8">
          <span className="t2">{formatPrice(price, locale, brand)}</span>
          {priceSuffix && <span className="font-normal text-ml"> {priceSuffix}</span>}
        </h2>
        {subtitle && <p className="s-text-subtle text-s mb-0">{subtitle}</p>}
      </div>
    </div>
  );
};

const PriceDouble = ({
  priceOne,
  priceTwo,
  textOne,
  textTwo,
  priceOneSubtitle,
}: {
  priceOne?: number;
  priceTwo?: number;
  textOne: string;
  textTwo: string;
  priceOneSubtitle?: string;
}) => {
  const { brand, locale } = useAppConfig();
  if (priceOne === undefined && !priceTwo) return null;
  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-2 border-t pt-40 mt-40">
        {!!priceOne && (
          <div className="flex flex-col">
            <p className="s-text-subtle mb-0">{textOne}</p>
            <h2 className="t2 mb-0 mt-8">{formatPrice(priceOne, locale, brand)}</h2>
            {priceOneSubtitle && <p className="s-text-subtle text-s mb-0">{priceOneSubtitle}</p>}
          </div>
        )}
        {!!priceTwo && (
          <div className="flex flex-col">
            <p className="s-text-subtle mb-0">{textTwo}</p>
            <h2 className="t2 mb-0 mt-8">{formatPrice(priceTwo, locale, brand)}</h2>
          </div>
        )}
      </div>
    </div>
  );
};

const PriceRange = ({ priceFrom, priceTo, text }: { priceFrom?: number; priceTo?: number; text: string }) => {
  const { locale, brand } = useAppConfig();
  if (priceFrom !== undefined) {
    return (
      <div>
        {priceFrom > 0 && (
          <div className="flex flex-col border-t pt-40 mt-40">
            <p className="s-text-subtle mb-0">{text}</p>
            <div className="flex flex-row mt-8">
              <h2 className="t2 mb-0">
                {priceTo !== undefined && priceTo > 0 ? (
                  <>
                    {formatNumber(priceFrom, locale)}
                    <span>{`\u00A0-\u00A0`}</span>
                    <span>{formatPrice(priceTo, locale, brand)}</span>
                  </>
                ) : (
                  formatPrice(priceFrom, locale, brand)
                )}
                <span>
                  {" "}
                  <Trans id="itemPageLayout.adPrice.rentPrice.label.suffix" message="per week" />
                </span>
              </h2>
            </div>
          </div>
        )}
      </div>
    );
  }

  return null;
};
